import React, { useState, useEffect } from "react";
import {
    BaseBox,
    BaseTypography,
} from "../../Common/UI/exports";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";

import { fetchTenants, addTenant, updateTenant, updateTenantStatus, deleteTenant } from "../../APIs/tenantManagementAPI";

import { appCache } from "../../Utils/appCache";

const GIT_PROVIDERS = [
    "AZURE",
    "GITHUB",
];

function TenantManagement() {
    const [tenants, setTenants] = useState([]);
    const [filterText, setFilterText] = useState("");

    // Dialog state (reused for create + edit)
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState("create"); // 'create' | 'edit'
    const [selectedTenant, setSelectedTenant] = useState(null); // tenant being edited
    const [tenantNameInput, setTenantNameInput] = useState("");
    const [tenantCodeInput, setTenantCodeInput] = useState("");
    const [saving, setSaving] = useState(false);

    // Configuration fields
    const [gitProvider, setGitProvider] = useState("AZURE");
    const [projectName, setProjectName] = useState("");
    const [organizationName, setOrganizationName] = useState("");

    const [gitDirectoryPath, setGitDirectoryPath] = useState("");
    const [awsAccountId, setAwsAccountId] = useState("");
    const [awsRegion, setAwsRegion] = useState("eu-central-1");
    const [apiVersion, setApiVersion] = useState("");

    // Delete dialog state
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [tenantToDelete, setTenantToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const handleFilterChange = (e) => setFilterText(e.target.value);

    // Normalize tenants to ensure { id?, name } shape
    const normalizeTenant = (r) => {
        if (!r || typeof r !== "object") return { id: undefined, name: String(r ?? ""), isActive: true };
        const statusValue = r.status ?? r.isActive ?? r.active ?? r.enabled ?? true;
        let isActive = true;
        if (typeof statusValue === "boolean") {
            isActive = statusValue;
        } else if (typeof statusValue === "string") {
            isActive = statusValue.toUpperCase() === "ACTIVE" || statusValue.toUpperCase() === "ENABLED";
        }
        return {
            ...r,
            name: r.name ?? r.tenantName ?? "",
            isActive,
        };
    };

    // Open dialog in Create mode
    const handleOpenCreate = () => {
        setDialogMode("create");
        setSelectedTenant(null);
        setTenantNameInput("");
        setTenantCodeInput("");
        setGitProvider("AZURE");
        setProjectName("");
        setOrganizationName("");
        setAwsAccountId("");
        setAwsRegion("eu-central-1");
        setApiVersion("");
        setIsDialogOpen(true);
    };

    // Open dialog in Edit mode
    const handleOpenEdit = (tenant) => {
        const normalized = normalizeTenant(tenant);
        setDialogMode("edit");
        setSelectedTenant(normalized);
        setTenantNameInput(normalized.name || "");
        setTenantCodeInput(normalized.code || normalized.name || "");
        setGitProvider(normalized.gitProvider || "");
        setProjectName(normalized.projectName || "");
        setOrganizationName(normalized.organizationName || "");

        setGitDirectoryPath(normalized.gitDirectoryPath || "");
        setAwsAccountId(normalized.awsAccountId || "");
        setAwsRegion(normalized.awsRegion || "");
        setApiVersion(normalized.apiVersion || "");
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        if (saving) return;
        setIsDialogOpen(false);
        setSelectedTenant(null);
        setTenantNameInput("");
        setTenantCodeInput("");
        setGitProvider("");
        setProjectName("");
        setOrganizationName("");
        setGitDirectoryPath("");
        setAwsAccountId("");
        setAwsRegion("");
        setApiVersion("");
        setDialogMode("create");
    };

    // Prevent duplicates by case-insensitive name match
    const nameExists = (name, ignoreId) => {
        const n = name.trim().toLowerCase();
        return tenants.some((r) => {
            const rr = normalizeTenant(r);
            if (ignoreId && rr.id === ignoreId) return false;
            return (rr.name || "").toLowerCase() === n;
        });
    };

    // Save handler (create or edit)
    const handleSave = async () => {
        const name = tenantNameInput.trim();
        const code = tenantCodeInput.trim();
        if (!name) {
            toast.warn("Please enter a tenant name.");
            return;
        }

        if (!code) {
            toast.warn("Please enter a tenant code.");
            return;
        }

        if (!gitProvider) {
            toast.warn("Please select a git provider.");
            return;
        }
        if (gitProvider !== "GITHUB" && !projectName.trim()) {
            toast.warn("Please enter a project name.");
            return;
        }
        if (!organizationName.trim()) {
            toast.warn("Please enter an organization name.");
            return;
        }

        if (!awsAccountId.trim()) {
            toast.warn("Please enter an AWS account ID.");
            return;
        }
        if (!awsRegion.trim()) {
            toast.warn("Please enter an AWS region.");
            return;
        }
        if (gitProvider !== "AZURE" && !apiVersion.trim()) {
            toast.warn("Please enter an API version.");
            return;
        }

        if (dialogMode === "create" && nameExists(name)) {
            toast.warn(`Tenant "${name}" already exists.`);
            return;
        }

        if (dialogMode === "edit") {
            const ignoreId = selectedTenant?.id;
            if (nameExists(name, ignoreId)) {
                toast.warn(`Tenant "${name}" already exists.`);
                return;
            }
        }

        try {
            setSaving(true);

            if (dialogMode === "create") {
                const tenantData = {
                    name: name,
                    code: code,
                    gitProvider: gitProvider,
                    projectName: gitProvider === "GITHUB" ? null : projectName.trim(),
                    organizationName: organizationName.trim(),
                    gitDirectoryPath: "/tmp/repositories/",
                    awsAccountId: awsAccountId.trim(),
                    awsRegion: awsRegion.trim(),
                    apiVersion: gitProvider === "AZURE" ? null : apiVersion.trim(),
                };
                const created = await addTenant(tenantData);
                const createdList = Array.isArray(created) ? created : [created];
                const normalizedCreated = createdList.map(normalizeTenant);

                const updatedTenants = [...tenants, ...normalizedCreated];
                setTenants(updatedTenants);
                appCache.tenants = updatedTenants;
                toast.success(`Tenant "${name}" created successfully.`);
                handleCloseDialog();
            } else {
                // edit mode
                const updatedData = {
                    name: name,
                    code: code,
                    gitProvider: gitProvider,
                    projectName: gitProvider === "GITHUB" ? null : projectName.trim(),
                    organizationName: organizationName.trim(),
                    gitDirectoryPath: gitDirectoryPath.trim(),
                    awsAccountId: awsAccountId.trim(),
                    awsRegion: awsRegion.trim(),
                    apiVersion: gitProvider === "AZURE" ? null : apiVersion.trim(),
                };

                const updated = await updateTenant(selectedTenant.id, updatedData);
                const normalizedUpdated = normalizeTenant(updated);

                // Fallback: if API doesn't return the updated entity,
                // synthesize with local selectedTenant and new name
                const finalUpdated =
                    normalizedUpdated && normalizedUpdated.name
                        ? normalizedUpdated
                        : { ...selectedTenant, name };

                const updatedTenants = tenants.map((r) => {
                    const rr = normalizeTenant(r);
                    if (rr.id != null && finalUpdated.id != null) {
                        return rr.id === finalUpdated.id ? finalUpdated : rr;
                    }
                    // If no ids, match by old name as a fallback (not ideal)
                    return rr.name === selectedTenant.name ? finalUpdated : rr;
                });

                setTenants(updatedTenants);
                appCache.tenants = updatedTenants;
                toast.success(`Tenant "${name}" updated successfully.`);
                handleCloseDialog();
            }
        } catch (error) {
            console.error("Error saving tenant:", error);
            toast.error(
                dialogMode === "create" ? "Failed to add new tenant." : "Failed to update tenant."
            );
        } finally {
            setSaving(false);
        }
    };

    const filteredTenants = tenants
        .map(normalizeTenant)
        .filter((tenant) => tenant.name.toLowerCase().includes(filterText.toLowerCase()));

    useEffect(() => {
        const loadTenants = async () => {
            if (appCache.tenants) {
                setTenants(appCache.tenants.map(normalizeTenant));
            } else {
                try {
                    const data = await fetchTenants();
                    const normalized = Array.isArray(data) ? data.map(normalizeTenant) : [];
                    setTenants(normalized);
                    appCache.tenants = normalized;
                } catch (error) {
                    console.error("Error loading Tenants:", error);
                    toast.error("Failed to load Tenants.");
                }
            }
        };
        loadTenants();
    }, []);

    // ---------- DELETE FLOW ----------
    const handleOpenDelete = (tenant) => {
        const normalized = normalizeTenant(tenant);
        setTenantToDelete(normalized);
        setIsDeleteOpen(true);
    };

    const handleCloseDelete = () => {
        if (deleting) return;
        setIsDeleteOpen(false);
        setTenantToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!tenantToDelete) return;

        try {
            setDeleting(true);

            // Prefer deleting by id; fallback to name if your API uses name
            await deleteTenant(tenantToDelete.id ?? tenantToDelete.name);

            // Remove from state
            setTenants((prev) => {
                const updated = prev.filter((r) => {
                    const rr = normalizeTenant(r);
                    const key = rr.id ?? rr.name;
                    const delKey = tenantToDelete.id ?? tenantToDelete.name;
                    return key !== delKey;
                });
                appCache.tenants = updated;
                return updated;
            });

            toast.success(`Tenant "${tenantToDelete.name}" deleted successfully.`);
            handleCloseDelete();
        } catch (error) {
            const serverMsg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.response?.data ||
                error.message;

            if (error?.response?.status === 409) {
                toast.error(
                    serverMsg ||
                    `Cannot delete "${tenantToDelete.name}" because it is assigned to users. Remove it from users first.`
                );
            } else {
                toast.error(serverMsg || "Failed to delete tenant. Please try again.");
            }
            console.error("Delete tenant error:", error);
        } finally {
            setDeleting(false);
        }
    };

    // ---------- TOGGLE STATUS FLOW ----------
    const handleToggleStatus = async (tenant) => {
        const normalized = normalizeTenant(tenant);
        const newStatus = !normalized.isActive;
        try {
            await updateTenantStatus(normalized.id, newStatus);

            setTenants((prev) => {
                const updated = prev.map((t) => {
                    const nt = normalizeTenant(t);
                    if (nt.id === normalized.id) {
                        return { ...nt, isActive: !normalized.isActive };
                    }
                    return t;
                });
                appCache.tenants = updated;
                return updated;
            });

            toast.success(`Tenant "${normalized.name}" ${newStatus ? "activated" : "deactivated"} successfully.`);
        } catch (error) {
            const serverMsg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.response?.data ||
                error.message;
            toast.error(serverMsg || "Failed to update tenant status.");
            console.error("Toggle status error:", error);
        }
    };

    // ---------- /DELETE FLOW ----------

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            <BaseBox
                sx={{
                    height: "calc(100vh - 140px)",
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: "1160px",
                    mx: "auto",
                    mt: 0,
                    px: 2,
                }}
            >
                <BaseTypography sx={{ fontSize: "1.2rem", mb: 2, fontWeight: 600 }}>
                    All Tenants
                </BaseTypography>

                {/* Filter & Create */}

                <BaseBox
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <BaseBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            width: "100%",
                        }}
                    >
                        <TextField
                            size="small"
                            placeholder="Filter Tenants..."
                            value={filterText}
                            onChange={handleFilterChange}
                            sx={{
                                minWidth: 300,
                                "& .MuiInputBase-root": {
                                    height: 32,         // ✅ match button height if needed
                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                            sx={{ whiteSpace: "nowrap", height: 32, minHeight: 32 }}
                        >
                            Create New Tenant
                        </Button>
                    </BaseBox>
                </BaseBox>

                {/* Tenants Table */}
                <BaseBox
                    sx={{
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                        height: "auto",
                        alignSelf: "flex-start",
                        maxHeight: "400px",
                        overflowY: "auto",
                    }}
                >
                    <table className="users-roles-table">
                        <thead>
                            <tr>
                                <th>Tenant</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTenants.length ? (
                                filteredTenants.map((tenant, idx) => {
                                    const normalized = normalizeTenant(tenant);
                                    const tenantName = normalized.name ?? "";
                                    const isActive = normalized.isActive;
                                    const rowKey = normalized.id ?? `${tenantName}-${idx}`;
                                    return (
                                        <tr key={rowKey}>
                                            <td className="action-col center" title={tenantName}>{tenantName}</td>
                                            <td className="action-col center">
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={isActive}
                                                            onChange={() => handleToggleStatus(normalized)}
                                                            size="small"
                                                        />
                                                    }
                                                    label="Active"
                                                />
                                            </td>
                                            <td className="action-col center">
                                                <Tooltip title="Edit tenant">
                                                    <IconButton
                                                        color="primary"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenEdit(tenant);
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Delete tenant">
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        sx={{ ml: 1.5 }} // space between icons
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenDelete(tenant); // <-- opens confirmation popup
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={3} className="empty">
                                        No Tenants match your filter.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </BaseBox>
            </BaseBox>

            {/* Create / Edit Tenant Dialog (reused) */}
            <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="xs">
                <DialogTitle>
                    {dialogMode === "create" ? "Create New Tenant" : "Update Tenant"}
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Tenant Name"
                        placeholder="Enter tenant name"
                        value={tenantNameInput}
                        onChange={(e) => setTenantNameInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleSave();
                            }
                        }}
                        disabled={saving}
                        margin="dense"
                    />
                    <TextField
                        fullWidth
                        label="Tenant Code"
                        placeholder="Enter tenant code"
                        value={tenantCodeInput}
                        onChange={(e) => setTenantCodeInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleSave();
                            }
                        }}
                        disabled={saving}
                        margin="dense"
                    />

                    <BaseTypography sx={{ mt: 2, mb: 1, fontWeight: 600, fontSize: "0.9rem" }}>
                        Configuration
                    </BaseTypography>

                    <TextField
                        select
                        fullWidth
                        label="Git Provider"
                        value={gitProvider}
                        onChange={(e) => setGitProvider(e.target.value)}
                        disabled={saving}
                        margin="dense"
                    >
                        {GIT_PROVIDERS.map((t) => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </TextField>
                    {gitProvider !== "GITHUB" && (
                        <TextField
                            fullWidth
                            label="Project Name"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            disabled={saving}
                            margin="dense"
                        />
                    )}
                    {gitProvider !== "AZURE" && (
                        <TextField
                            fullWidth
                            label="API Version"
                            value={apiVersion}
                            onChange={(e) => setApiVersion(e.target.value)}
                            disabled={saving}
                            margin="dense"
                        />
                    )}
                    <TextField
                        fullWidth
                        label="Organization Name"
                        value={organizationName}
                        onChange={(e) => setOrganizationName(e.target.value)}
                        disabled={saving}
                        margin="dense"
                    />

                    <TextField
                        fullWidth
                        label="Git Directory Path"
                        value="/tmp/repositories/"
                        disabled
                        margin="dense"
                    />
                    <TextField
                        fullWidth
                        label="AWS Account ID"
                        value={awsAccountId}
                        onChange={(e) => setAwsAccountId(e.target.value)}
                        disabled={saving}
                        margin="dense"
                    />
                    <TextField
                        fullWidth
                        label="AWS Region"
                        value={awsRegion}
                        onChange={(e) => setAwsRegion(e.target.value)}
                        disabled={saving}
                        margin="dense"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={saving} variant="outlined">
                        Close
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={
                            !tenantNameInput.trim() ||
                            !tenantCodeInput.trim() ||
                            !gitProvider ||
                            (gitProvider !== "GITHUB" && !projectName.trim()) ||
                    !organizationName.trim() ||
                            !awsAccountId.trim() ||
                            !awsRegion.trim() ||
                            (gitProvider !== "AZURE" && !apiVersion.trim()) ||
                            saving
                        }
                        variant="contained"
                    >
                        {saving ? "Saving..." : dialogMode === "create" ? "Save" : "Update"}
                    </Button>
                </DialogActions>
            </Dialog>

{/* Delete Tenant Confirmation Dialog */}
            <Dialog open={isDeleteOpen} onClose={handleCloseDelete} fullWidth maxWidth="xs">
                <DialogTitle>Delete Tenant</DialogTitle>
                <DialogContent dividers>
                    <BaseTypography sx={{ mb: 1 }}>
                        You're about to delete the tenant <b>{tenantToDelete?.name}</b>.
                    </BaseTypography>

                    <BaseTypography sx={{ mt: 2, color: "error.main" }}>
                        This action is <b>irreversible</b>. Users assigned to this tenant will permanently lose all associated permissions.
                    </BaseTypography>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleCloseDelete} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        disabled={deleting}
                    >
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default TenantManagement;